import axios from 'axios';
import moment from 'moment';
import { notifyAtatus } from '@/utils/atatus';
import {
  clearAll,
  getLocalStorageByKey,
  setLocalStorageByKey,
  LOCALSTORAGE_KEY,
} from '../utils/auth';
import { IDLE_TIMER_TIMEOUT } from '../constant/constant';
import { fetchRefreshToken, postError } from './auth';
import { ERROR_LOG_SUBTYPE, ERROR_LOG_TYPE } from '../constant/enum';
import { currentRelativeUrl } from '../utils/url';
import { notifySalesforceOfWrite } from '../utils/salesforceEmbedHost';

export enum API_STATUS_CODE {
    UNAUTHORIZE = 401,
    FORBIDDEN = 403,
    NOT_ACCEPTABLE = 406,
    SUCCESS = 200
}

enum FORBIDDEN_SUB_STATUS_CODE {
    EXPIRED_JWT = 'EXPIRED_JWT',
    MALFORMED_JWT = 'MALFORMED_JWT',
    USER_NOT_FOUND = 'USER_NOT_FOUND',
    UNAUTHORIZE_USER = 'You are not authorized to login in to the application, kindly contact the system administrator.',
    UNAUTHORIZE_TOKEN = 'failed to get access token'
}

const publicURLS = [
  '/agreement/authorizeagreement',
  '/agreement/terms-and-conditions/gettermandcondition',
  '/configuration/getconfiguration',
  '/jetcard/cardclientview',
];

export const SKIP_403_REDIRECT_HEADER = 'x-skip-403-redirect';

axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;

axios.interceptors.request.use((config: any) => {
  let isPublicURl = false;
  publicURLS.forEach((url: string) => {
    if (config.url.includes(url)) {
      isPublicURl = true;
    }
  });
  if(config.url.split('/').includes('pdf')){
    isPublicURl = false;
  }

  const token = getLocalStorageByKey(LOCALSTORAGE_KEY.TOKEN);
  if (config.headers && token && !isPublicURl) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // To identify which api is public
  if (config.headers && config.headers.isPublic) {
    delete config.headers.Authorization;
    delete config.headers.isPublic;
  }
  // Todo: For future use, currently it's generating CORS Error
  // config.headers['x-request-id'] = shortId.generate();

  if (token) {
    const lastApiTimestamp = getLocalStorageByKey(LOCALSTORAGE_KEY.LAST_API_TIMESTAMP);
    if (lastApiTimestamp) {
      const currentTime = moment();
      const lastApiTimestampMoment = moment(lastApiTimestamp, moment.ISO_8601);
      if (!lastApiTimestampMoment.isValid() || currentTime.diff(lastApiTimestampMoment, 'seconds') > IDLE_TIMER_TIMEOUT) {
        logout(); // kill the session due to inactivity
      }
    }
  }

  return config;
});

let isTokenRefreshing = false; // For avoiding multiple refresh token call
let requestCaching: Array<(token: string) => void> = [];
axios.interceptors.response.use(
  (response) => {
    setLocalStorageByKey(LOCALSTORAGE_KEY.LAST_API_TIMESTAMP, moment().toISOString());
    notifySalesforceOfWrite(response.config?.method);
    return Promise.resolve(response);
  },
  (error) => {
    const { config, response } = error;
    const originalRequest = config;

    // the request was aborted, don't do anything special
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    if (!response || response.status === API_STATUS_CODE.FORBIDDEN) {
      // skip 403 redirect if override header is present or request was aborted
      if (
        originalRequest.headers?.[SKIP_403_REDIRECT_HEADER] ||
              axios.isCancel(error)
      ) {
        return Promise.reject(error);
      }

      const errorStackJSONString = JSON.stringify({
        type: ERROR_LOG_TYPE.FRONTEND_ERROR,
        subType: ERROR_LOG_SUBTYPE.API,
        error: error.message,
        request: {
          url: originalRequest.url,
          data: originalRequest.data,
          method: originalRequest.method,
        },
        response: {
          status: response?.status ?? null,
          data: response?.data ?? null,
        },
      });
      postError(errorStackJSONString);
      notifyAtatus(new Error(`API ${response?.status}: ${originalRequest.method?.toUpperCase()} ${originalRequest.url}`), {
        url: originalRequest.url,
        method: originalRequest.method,
        status: response?.status,
        responseData: response?.data,
      });
      window.location.replace('/403');
      return Promise.reject(error);
    } else if (response.status === API_STATUS_CODE.UNAUTHORIZE) {
      if (response.data.responseMessage === FORBIDDEN_SUB_STATUS_CODE.EXPIRED_JWT) {
        if (!isTokenRefreshing) {
          isTokenRefreshing = true;
          fetchRefreshToken((_, isError) => {
            if (isError) {
              logout();
            } else {
              setTimeout(() => {
                recallCachedAPIs();
                requestCaching = [];
                isTokenRefreshing = false;
              }, 1000); // To handle parallel API Calls
            }
          });
        }
        // Cache all API call and resolve only after token refreshed!
        return new Promise((resolve) => {
          subscribeToken((token: string) => {
            originalRequest.headers.Authorization = 'Bearer ' + token;
            resolve(axios(originalRequest));
          });
        });
      } else if (
        response.data.responseMessage === FORBIDDEN_SUB_STATUS_CODE.UNAUTHORIZE_USER ||
                response.data.responseMessage === FORBIDDEN_SUB_STATUS_CODE.UNAUTHORIZE_TOKEN
      ) {
        return Promise.reject(error);
      } else {
        logout();
      }
    } else {
      return Promise.reject(error);
    }
  }
);

// Cached all Http calls which one failed
const subscribeToken = (cb: (token: string) => void) => {
  requestCaching.push(cb);
};

// Recall once the access token refreshed
const recallCachedAPIs = () => {
  const token = getLocalStorageByKey(LOCALSTORAGE_KEY.TOKEN);
  if (token) {
    requestCaching.forEach(cb => cb(token));
  }
};

const logout = () => {
  clearAll();
  const redirectUri = encodeURIComponent(currentRelativeUrl());
  window.location.href = `${window.location.origin}/login?return_url=${redirectUri}`;
};

const useAxios = async ({
  url,
  method,
  body = null,
  headers = null,
  signal = null,
  params = null,
  responseType = null,
}: any) => {
  let response: any = null;
  let error = '';
  let loading = true;
  let status = null;
  const api: any = axios;
  const parsedBody = typeof body === 'string' ? JSON.parse(body) : body;
  const parsedHeaders = typeof headers === 'string' ? JSON.parse(headers) : headers;
  const parsedParams = typeof params === 'string' ? JSON.parse(params) : params;
  const config = {
    ...(parsedParams && { params: parsedParams }),
    ...(parsedHeaders && { headers: parsedHeaders }),
    ...(signal && { signal }),
    ...(responseType && { responseType }),
  };
  const fetchData = async () => {
    const request =
      method === 'get'
        ? api.get(url, config)
        : api[method](url, parsedBody, config);
    await request
      .then((res: any) => {
        status = res.status;
        response = res.data;
      })
      .catch((err: any) => {
        response = err?.response?.data ?? null;
      })
      .finally(() => {
        loading = false;
      });
  };

  await fetchData();

  return { response, error, loading, status };
};

export const AxiosService = () => axios;

export default useAxios;
